<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.adminUser.actions.image.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: 'Delete existing photo', type: 'confirm', variant: 'danger', callback: handleDelete, validate: false },
      { text: $t('asset.upload'), type: 'confirm', callback: handleConfirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left">
      <o-form v-if="!loading" ref="form">
        <o-field-group :label="$t('general.image')" :name="$t('general.image')" class="block pb-3">
          <o-upload v-model="image" />
        </o-field-group>
      </o-form>
    </div>

    <template #loading>
      <div v-if="loading" class="w-full max-w-sm mx-auto">
        <o-progress v-if="progress" :progress="progress" :label="$t('general.uploading')" />
        <o-loader v-else class="mx-auto" />
      </div>
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  OUpload,
  OProgress,
} from '@/components/elements'

interface Props {
  adminUser: any
  portal?: string
}

interface Emits {
  (e: 'success'): void
  (e: 'update:adminUser', adminUser: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const image = ref<File | null>(null)
const loading = ref(false)
const progress = ref(0)

const upload = computed({
  get() {
    if (image.value instanceof File) {
      return image.value
    }
    return null
  },
  set(value: File | null) {
    image.value = value
  },
})

const info = computed(() => {
  return props.adminUser ? $i18n.t('components.adminUser.actions.image.infoWithName') : $i18n.t('components.adminUser.actions.image.info')
})

const open = () => {
  image.value = null

  dialog.value.open()
}

const validate = () => {
  return !!image.value
}

const handleConfirm = async () => {
  loading.value = true

  try {
    const imageFile = upload.value
    if (!imageFile) return

    const response = await $api.adminUser.addPhoto(
      props.adminUser,
      imageFile,
      ($event: any) => handleOnUploadProgress($event),
    )

    if (response.data) {
      $notification.toast({
        title: $i18n.t('components.adminUser.actions.image.updateSuccess'),
        text: $i18n.t('asset.successInfo2', { label: props.adminUser.displayName }),
        variant: 'success',
      })

      emit('success')
      emit('update:adminUser', response.data)

      loading.value = false
      progress.value = 0

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleDelete = async () => {
  loading.value = true

  try {
    const response = await $api.adminUser.deletePhoto(props.adminUser)

    if (response.data) {
      $notification.toast({
        title: $i18n.t('asset.deleteSuccess'),
        text: $i18n.t('asset.deleteSuccessInfo'),
        variant: 'success',
      })

      emit('success')
      emit('update:adminUser', response.data)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleOnUploadProgress = ($event: { loaded: number; total: number }) => {
  progress.value = parseInt(String(Math.round(($event.loaded / $event.total) * 100)))
}

defineExpose({ open })
</script>
